<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Header from '../components/HeaderView.vue'
import Footer from '../components/FooterView.vue'
import HomeSection2 from '../components/HomeSection2.vue'
import DemoSection from '../components/DemoSection.vue'
import TrustBar from '../components/TrustBar.vue'
import CMMSShowcaseSection from '../components/CMMSShowcaseSection.vue'
import StickyMobileSection from '../components/StickyMobileSection.vue'
import AccessAnywhereSection from '../components/AccessAnywhereSection.vue'
import { useSEO } from '../composables/useSEO'

gsap.registerPlugin(ScrollTrigger)

const { updateMetaTags } = useSEO()

// --- Refs del hero (animación de celulares) ---
const mockupsContainerRef = ref<HTMLElement | null>(null)
const leftMockupRef = ref<HTMLElement | null>(null)
const rightMockupRef = ref<HTMLElement | null>(null)
const centerMockupRef = ref<HTMLElement | null>(null)
const accentTextRef = ref<HTMLElement | null>(null)

onMounted(() => {
  // SEO
  updateMetaTags({
    title: 'Fourin - Software CMMS/GMAO en Colombia | Gestión de Activos',
    description: 'Software CMMS/GMAO líder en Colombia y LATAM. Fourintrack te ayuda a gestionar activos, mantenimiento preventivo y predictivo. Reduce paradas, costos y riesgos. Prueba gratis 14 días.',
    keywords: 'CMMS Colombia, GMAO Latinoamérica, software mantenimiento, gestión de activos, mantenimiento preventivo, software órdenes de trabajo, CMMS en la nube, mantenimiento predictivo',
    url: 'https://fourin.co',
    canonical: 'https://fourin.co',
    type: 'website',
    organization: true,
  })

  // --- Animación del hero ---
  gsap.to(accentTextRef.value, {
    backgroundPosition: '0% center',
    duration: 1,
    delay: 0.6,
    ease: 'power2.inOut',
  })

  const mm = gsap.matchMedia()

  mm.add('(min-width: 768px)', () => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: mockupsContainerRef.value,
        start: 'top 45%',
        end: 'top 5%',
        scrub: 1,
      },
    })

    tl.to(leftMockupRef.value, { xPercent: -80, yPercent: 5, rotation: -20, ease: 'power2.out' }, 0)
    tl.to(rightMockupRef.value, { xPercent: 80, yPercent: 5, rotation: 20, ease: 'power2.out' }, 0)
    tl.to(centerMockupRef.value, { yPercent: -6, ease: 'power2.out' }, 0)
  })

  onUnmounted(() => {
    mm.revert()
    ScrollTrigger.getAll().forEach((t) => t.kill())
  })

  window.scrollTo({ top: 0, behavior: 'smooth' })
})
</script>

<template>
  <div class="landing-page">
    <Header />

    <main class="main-content">
      <section class="hero-section">
        <!-- Badge Superior -->
        <div class="hero-badge animate-fade-in-up">
          <span class="hero-badge-label">Nuevo</span>
          <span class="hero-badge-text">
            Software CMMS en la nube con dispositivos inteligentes y geolocalización
            <svg class="hero-badge-icon" fill="currentColor" viewBox="0 0 256 256">
              <path
                d="M216,120a8,8,0,0,1-8,8,64.07,64.07,0,0,0-64,64,8,8,0,0,1-16,0,64.07,64.07,0,0,0-64-64,8,8,0,0,1,0-16,64.07,64.07,0,0,0,64-64,8,8,0,0,1,16,0,64.07,64.07,0,0,0,64,64A8,8,0,0,1,216,120Z">
              </path>
            </svg>
            <span class="hero-badge-arrow">›</span>
          </span>
        </div>

        <!-- Titulo H1 -->
        <h1 class="hero-title animate-fade-in-up">
          Software <span class="text-accent text-accent-animated" ref="accentTextRef">CMMS/GMAO</span><br />
          <span class="text-dark">para empresas</span>
        </h1>

        <!-- Subtitulo -->
        <p class="hero-subtitle animate-fade-in-up">
          Una plataforma profesional para gestionar mantenimiento industrial y activos
          <br> empresariales.
        </p>

        <!-- Bloque GSAP Scroll Animado de Celulares -->
        <div class="mockups-container animate-fade-in-up" ref="mockupsContainerRef">
          <div class="hero-mockup-wrapper mockup-left" ref="leftMockupRef">
            <img src="../assets/mockups/iphoneleft.png" alt="Módulos de mantenimiento" class="mockup-image-left" />
          </div>
          <div class="hero-mockup-wrapper mockup-right" ref="rightMockupRef">
            <img src="../assets/mockups/iphoneright.png" alt="Reportes detallados" class="mockup-image-right" />
          </div>
          <div class="hero-mockup-wrapper mockup-center" ref="centerMockupRef">
            <img src="../assets/mockups/iphonemiddle.png" alt="Dashboard de Operario" class="mockup-image-center" />
          </div>
        </div>
      </section>

      <div class="cta-container animate-fade-in-up">
        <button class="btn-cta group">
          Solicita una Demo
          <svg class="btn-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
          </svg>
        </button>
      </div>

      <CMMSShowcaseSection />

      <TrustBar />

      <HomeSection2 />

      <StickyMobileSection />

      <AccessAnywhereSection />

      <DemoSection />
    </main>

    <Footer />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

/* Global Layout */
.landing-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  font-family: 'Manrope', sans-serif;
  width: 100%;
}

.main-content {
  flex: 1;
  width: 100%;
  overflow-x: clip;
  display: flex;
  flex-direction: column;
}

/* Hero Section */
.hero-section {
  width: 100%;
  position: relative;
  padding: 90px 0 0;/* No cambiar por 180 */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 1400px;
  margin: 0 auto;
}

/* Utilities */
.text-accent {
  color: #129BEF;
  font-weight: 700;
}

.text-accent-animated {
  background: linear-gradient(90deg, #129BEF 0%, #129BEF 45%, #173854 65%, #000000 100%);
  background-size: 300% auto;
  background-position: 100% center;
  color: transparent !important;
  -webkit-background-clip: text;
  background-clip: text;
}

.text-dark {
  color: #111827;
  font-weight: 700;
}

/* Badge Superior */
.hero-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  margin-bottom: 20px;
}

@media (min-width: 768px) {
  .hero-badge {
    flex-direction: row;
  }
}

.hero-badge-label {
  background-color: #129BEF;
  color: #ffffff;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 9999px;
}

.hero-badge-text {
  font-size: 18px;
  color: #4B5563;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.hero-badge-icon {
  width: 16px;
  height: 16px;
  color: #F59E0B;
}

.hero-badge-arrow {
  color: #129BEF;
  font-weight: 700;
  margin-left: 4px;
  cursor: pointer;
}

.hero-badge-arrow:hover {
  text-decoration: underline;
}

/* Titles */
.hero-title {
  font-size: 40px;
  line-height: 1.1;
  letter-spacing: -0.04em;
  color: #111827;
  font-weight: 800;
  margin: 0 auto 32px auto;
  max-width: 1000px;
}

@media (min-width: 768px) {
  .hero-title {
    font-size: 72px;
  }
}

.hero-subtitle {
  font-size: 20px;
  color: #6B7280;
  line-height: 1.6;
  font-weight: 500;
  margin: 0 auto 60px auto;
}

/* Mockups Layout */
.mockups-container {
  position: relative;
  width: 100%;
  max-width: 1000px;
  height: 1000px;
  margin: 0 auto;
}

@media (min-width: 1024px) {
  .mockups-container {
    height: 750px;
  }
}

.hero-mockup-wrapper {
  position: absolute;
  top: 0;
  transform-origin: center bottom;
}

.mockup-image-left {
  width: 400px;
  display: block;
}

.mockup-image-right {
  width: 400px;
  display: block;
}

.mockup-image-center {
  width: 900px;
  display: block;
}

.mockup-center {
  z-index: 10;
  left: 50%;
  margin-left: -450px;
}

.mockup-left {
  display: none;
  z-index: 5;
  left: 70%;
  margin-left: -200px;
}

.mockup-right {
  display: none;
  z-index: 5;
  left: 30%;
  margin-left: -200px;
}

@media (min-width: 768px) {
  .mockup-left {
    display: block;
  }

  .mockup-right {
    display: block;
  }
}

/* Tablet: escalar los 3 teléfonos para que no desborden */
@media (min-width: 768px) and (max-width: 1023px) {
  .mockups-container {
    height: 560px;
  }
  .mockup-image-center {
    width: 600px;
  }
  .mockup-center {
    margin-left: -300px;
  }
  .mockup-image-left,
  .mockup-image-right {
    width: 280px;
  }
  .mockup-left,
  .mockup-right {
    margin-left: -140px;
  }
}

/* Mobile: solo el teléfono central, reducido */
@media (max-width: 767px) {
  .hero-section {
    padding: 40px 16px 0;
  }
  .hero-badge {
    flex-direction: row;
  }
  .hero-badge-text {
    font-size: 12px;
    text-align: start;
  }
  .hero-subtitle {
    font-size: 15px;
    margin-bottom: 40px;
  }
  .mockups-container {
    height: 300px;
  }
  .mockup-image-center {
    width: 260px;
  }
  .mockup-center {
    margin-left: -130px;
  }
}

/* CTA */
.cta-container {
  position: relative;
  z-index: 30;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-color: #ffffff;
  padding-top: 30px;
  height: 150px;
  box-shadow: 0 -35px 40px 20px rgb(255 255 255);
  border-top: 2px solid;
  border-image: linear-gradient(to right, #ffffff, #e2e8f0, #ffffff) 1;
}

.btn-cta {
  background-color: #129BEF;
  color: #ffffff;
  font-weight: 700;
  font-size: 18px;
  padding: 16px 36px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: inherit;
}

.btn-cta:hover {
  background-color: #0c82ce;
  transform: translateY(-4px);
}

.btn-arrow {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.btn-cta:hover .btn-arrow {
  transform: translateX(4px);
}

/* Animations */
.animate-fade-in-up {
  opacity: 0;
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.hero-badge.animate-fade-in-up {
  animation-delay: 0.1s;
  animation-duration: 0.6s;
}

.hero-title.animate-fade-in-up {
  animation-delay: 0.2s;
}

.hero-subtitle.animate-fade-in-up {
  animation-delay: 0.3s;
}

.mockups-container.animate-fade-in-up {
  animation-delay: 0.4s;
  animation-duration: 1s;
}

.cta-container.animate-fade-in-up {
  animation-delay: 0.7s;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
